import { useEffect, useRef, useState } from "react";
import { useAppStore } from "../../store/app";
import {
  contactInitials,
  formatLastSeen,
  formatMessageTime,
  presenceFromLastSeen,
} from "../../types";
import { AvatarOrb } from "../ui/AvatarOrb";
import { DeliveryTicks } from "./DeliveryTicks";
import styles from "./ChatView.module.css";

export function ChatView() {
  const selectedAriaId = useAppStore((s) => s.selectedAriaId);
  const contacts = useAppStore((s) => s.contacts);
  const messages = useAppStore((s) => s.messages);
  const sendMessage = useAppStore((s) => s.sendMessage);
  const backToRoster = useAppStore((s) => s.backToRoster);
  const pairing = useAppStore((s) => s.pairing);
  const deviceSession = useAppStore((s) => s.deviceSession);
  const startPairing = useAppStore((s) => s.startPairing);
  const [text, setText] = useState("");
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollRafRef = useRef<number | null>(null);

  const contact = contacts.find((c) => c.ariaId === selectedAriaId);
  const last = messages[messages.length - 1];
  const streaming = last?.streaming === true;
  // Coalesce rapid chunk updates: rAF + length key so we follow growth without smooth jank.
  const scrollKey = `${messages.length}:${last?.id ?? ""}:${last?.text.length ?? 0}:${streaming ? 1 : 0}`;

  useEffect(() => {
    if (scrollRafRef.current != null) {
      cancelAnimationFrame(scrollRafRef.current);
    }
    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      bottomRef.current?.scrollIntoView({
        behavior: streaming ? "auto" : "smooth",
      });
    });
    return () => {
      if (scrollRafRef.current != null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [scrollKey, streaming]);

  if (!selectedAriaId || !contact) return null;

  const online = presenceFromLastSeen(contact.lastSeenAt) === "online";
  const needsPairing = !deviceSession && pairing.phase !== "awaiting_approval";

  const subtitle = contact.typing
    ? "thinking…"
    : online
      ? contact.warm === false
        ? "online · warming up"
        : "online"
      : formatLastSeen(contact.lastSeenAt);

  const onSend = () => {
    if (!text.trim()) return;
    void sendMessage(text);
    setText("");
  };

  return (
    <div className={styles.chatWrap}>
      {needsPairing ? (
        <div className={styles.pairBanner}>
          <div>This device is not paired with {contact.name}.</div>
          <button type="button" className={styles.send} style={{ width: "auto", borderRadius: 8, marginTop: 8, padding: "0.4rem 0.8rem" }} onClick={() => void startPairing(selectedAriaId)}>
            Pair device
          </button>
        </div>
      ) : null}

      {pairing.phase === "awaiting_approval" ? (
        <div className={styles.pairBanner}>
          <div>Approve on AARIA TUI:</div>
          <div className={styles.pairCode}>{pairing.code}</div>
          <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
            <code>/console pair {pairing.code}</code>
          </div>
        </div>
      ) : null}

      <header className={styles.header}>
        <button type="button" className={styles.back} onClick={backToRoster} aria-label="Back">
          ←
        </button>
        <AvatarOrb initials={contactInitials(contact.name)} online={online} small />
        <div className={styles.info}>
          <h2>{contact.name}</h2>
          <p className={online ? styles.online : ""}>{subtitle}</p>
        </div>
      </header>

      <div className={styles.messages}>
        {messages.map((m) => (
          <div
            key={`${m.role}:${m.id}`}
            className={`${styles.row} ${m.role === "user" ? styles.rowUser : ""}`}
          >
            {m.role === "assistant" ? (
              <AvatarOrb initials="AR" online={online} small />
            ) : null}
            <div>
              <div
                className={`${styles.bubble} ${m.role === "user" ? styles.user : styles.assistant} ${m.streaming ? styles.streaming : ""}`}
              >
                {m.text || (m.streaming ? "…" : "")}
              </div>
              <div className={styles.meta}>
                {formatMessageTime(m.ts)}
                {m.role === "user" ? <DeliveryTicks status={m.delivery} /> : null}
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className={styles.inputBar}>
        <input
          type="text"
          placeholder="Message…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          aria-label="Message"
        />
        <button
          type="button"
          className={styles.send}
          onClick={onSend}
          disabled={!text.trim()}
          aria-label="Send"
        >
          ➤
        </button>
      </div>
    </div>
  );
}
